import { Box, Stack, Typography, Fade } from "@mui/material";
import { SignalWifiOffRounded, ErrorOutlineRounded } from "@mui/icons-material";
import { useEffect, useState, useRef, type ReactNode } from "react";
import { toast } from "sonner";
import { type NetworkStatus, useNetworkStatus } from "Hooks/useNetworkStatus";
import { useResource, Resources, type TFunction } from "Translations/Resources";
import { MooneLogo } from "Components/Layout/OfflineOverlay/MooneLogo";
import { useLocation } from "react-router-dom";
import { shouldSuppressOverlay } from "Components/Layout/OfflineOverlay/OfflineOverlayConfig";

const RESOURCES = Resources.Offline;

const getContentForNetworkStatus = (
  networkStatus: NetworkStatus,
  t: TFunction,
) => {
  const contentLookup: Record<
    NetworkStatus,
    { icon: ReactNode; title: string; message: string }
  > = {
    online: { icon: null, title: "", message: "" },
    offline: {
      icon: <SignalWifiOffRounded color="warning" sx={{ fontSize: 80 }} />,
      title: t(RESOURCES.Title),
      message: t(RESOURCES.NoInternet),
    },
    serverUnreachable: {
      icon: <ErrorOutlineRounded color="error" sx={{ fontSize: 80 }} />,
      title: t(RESOURCES.ServerUnreachable.Title),
      message: t(RESOURCES.ServerUnreachable.Message),
    },
  };

  return contentLookup[networkStatus];
};

/**
 * Full-screen overlay that appears when the app loses network connectivity or the server is unreachable.
 */
export const OfflineOverlay = () => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [hasBeenOffline, setHasBeenOffline] = useState(false);
  const hasShownReconnectToast = useRef(false);
  const { networkStatus } = useNetworkStatus();
  const { pathname } = useLocation();
  const { t } = useResource();

  const isSuppressed = shouldSuppressOverlay(networkStatus, pathname);
  const isOffline = networkStatus !== "online";
  const reconnectMessage = t(RESOURCES.Reconnected);

  useEffect(
    function handleNetworkStatusChange() {
      if (isOffline) {
        setHasBeenOffline(true);
        setIsOverlayVisible(true);
        hasShownReconnectToast.current = false;
        return;
      }

      const isBackOnline =
        hasBeenOffline && !isOffline && !hasShownReconnectToast.current;

      if (isBackOnline) {
        hasShownReconnectToast.current = true;
        toast.info(reconnectMessage);
        setIsOverlayVisible(false);
      }
    },
    [isOffline, hasBeenOffline, reconnectMessage],
  );

  useEffect(
    function disableAppScrolling() {
      if (isOverlayVisible) {
        document.body.style.overflow = "hidden";
      }

      return () => {
        document.body.style.overflow = "";
      };
    },
    [isOverlayVisible],
  );

  if (!isOverlayVisible || isSuppressed) {
    return null;
  }

  const { icon, title, message } = getContentForNetworkStatus(networkStatus, t);

  return (
    <Fade in={isOverlayVisible} timeout={300}>
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={9999}
        bgcolor="common.white"
      >
        <Box position="absolute" top={20} left={16}>
          <MooneLogo />
        </Box>
        <Stack
          spacing={4}
          alignItems="center"
          p={4}
          maxWidth={400}
          textAlign="center"
        >
          {icon}
          <Typography variant="h4" color="common.black" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body1" color="common.black" lineHeight={1.6}>
            {message}
          </Typography>
        </Stack>
      </Box>
    </Fade>
  );
};
